import { AreasGet, AreasManagerGet } from "#core/Services/api_areas"
import { useApi } from "#hooks/useApi"
import { useEffect, useMemo } from "react"
import InputAutocomplete from "./InputAutocomplete"

/**
 * InputAutocompleteAreas
 *
 * Componente de autocompletado de áreas/departamentos.
 *
 * Este componente encapsula un `<InputAutocomplete>` que muestra un listado de áreas,
 * obtenidas mediante una llamada API (`AreasGet`) o recibidas por props.
 *
 * @component
 *
 * @param {Object} props - Props del componente.
 * @param {any} value - Valor actual del campo (puede ser un objeto, un string o un arreglo, según configuración de `multiple`).
 * @param {Function} setValue - Función para actualizar el valor seleccionado.
 * @param {Array<Object>} [props.data] - Lista de áreas a mostrar. Si se provee, no se realizará la llamada API.
 * @param {boolean} [props.loading] - Estado de carga externo opcional. Si se provee, tiene prioridad sobre el loading interno.
 * @param {boolean} [props.multiple] - Si `true`, permite seleccionar múltiples opciones.
 * @param {Function} [props.getData] - Callback que recibe el objeto completo de respuesta de la API cuando cambia.
 * @param {Function} [props.getLoading] - Callback que recibe el estado de carga cuando cambia.
 * @param {any} [props.*] - Cualquier otra prop adicional se pasa a `<InputAutocomplete>`.
 *
 * @returns {JSX.Element} Autocompletado de áreas con manejo de carga y datos.
 */
const InputAutocompleteAreas = ({ label, value, setValue, ...props }) => {
	const get = useMemo(() => {
		return props.manager ? AreasManagerGet : AreasGet
	}, [props.manager])

	const { data, loading } = useApi(get, { autoFetch: !props?.data })

	const areas = useMemo(() => {
		return props?.data ?? data?.areas ?? []
	}, [data, props.data])

	useEffect(() => {
		if (typeof props.getData === "function") props.getData(data)
		if (typeof props.getLoading === "function") props.getLoading(loading)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, loading])

	return (
		<InputAutocomplete
			label={label || "Departamento"}
			options={areas}
			loading={props.loading ? props.loading : loading}
			multiple={props.multiple}
			setValue={setValue}
			value={value}
			minWidth={props.minWidth}
			fullWidth={props.fullWidth}
		/>
	)
}
export default InputAutocompleteAreas
